/**
 * Modal for cloning a patient profile to another company.
 * Flow: select patient → select target org/company (impersonation-aware) → clone.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useDispatch, useSelector } from "react-redux";

import WithRouter from "../../hoc/WithRouter";
import WithRoutify from "../../hoc/WithRoutify";

import DropDownService from "../../services/DropDownService";
import EmployeeCloneService from "../../services/EmployeeCloneService";
import EmployeeService from "../../services/EmployeeService";
import OrganizationService from "../../services/OrganizationService";
import UserService from "../../services/UserService";
import FormInputs from "../UI/FormInputs";
import { convertUnderScoreStrings, formatPhoneNumberUS, validateEmail } from "../../utils/commonUtils";
import { isGlobalAdmin } from "../../utils/aclUtils";
import {
  cloneEmployeeProfile,
  reassignEmployeeProfile,
  clearLastCloneResult,
  selectCloneInProgress,
  selectLastCloneResult,
} from "../../store/EmployeeCloneSlice";

const WORKFLOW_CLONE = "clone";
const WORKFLOW_TRANSFER = "transfer";

const DEFAULT_COPY_OPTIONS = {
  clone_demographics: true,
  clone_insurance: true,
  clone_employer_forms: true,
};

/** Identity fields required to create a usable patient profile in the target company. */
const COPY_REQUIRED_KEY = "clone_demographics";

const COPY_OPTION_DEFS = [
  {
    key: COPY_REQUIRED_KEY,
    title: "Demographics & identification",
    summary: "Name, DOB, gender, contact, address, ID fields, ID picture, and lab/drug screen notification preference",
    icon: "fa-id-card",
    required: true,
  },
  {
    key: "clone_insurance",
    title: "Insurance",
    summary: "Carrier, member/group numbers, and insurance card images",
    icon: "fa-medkit",
  },
  {
    key: "clone_employer_forms",
    title: "Employer details",
    summary: "Job title, start date, supervisor, benefits, and license info",
    icon: "fa-briefcase",
  },
];

const validatePortalEmailInput = (email, profileEmail, needsCustom) => {
  if (!needsCustom) return null;
  const trimmed = (email || "").trim();
  if (!trimmed) {
    return "Enter a portal login email for the new profile.";
  }
  if (!validateEmail(trimmed)) {
    return "Please enter a valid email address.";
  }
  if (
    profileEmail &&
    trimmed.toLowerCase() === String(profileEmail).toLowerCase()
  ) {
    return "Use a different email — this address is already registered for portal login.";
  }
  return null;
};

const CopyOptionCard = ({ option, checked, onToggle, disabled }) => {
  const { key, title, summary, icon, required } = option;
  const lockedRequired = required && checked;
  const cardDisabled = disabled || lockedRequired;

  return (
    <button
      type="button"
      className={`clone-copy-option w-100 text-start border rounded-3 p-3 ${
        checked ? "clone-copy-option--active" : ""
      } ${disabled ? "opacity-50 pe-none" : ""} ${lockedRequired ? "pe-none" : ""}`}
      onClick={() => !cardDisabled && onToggle(key)}
      aria-pressed={checked}
      aria-disabled={cardDisabled || undefined}
      title={lockedRequired ? "Required — demographics are always copied" : undefined}
      style={{
        backgroundColor: checked ? "#eef4ff" : "#fff",
        borderColor: checked ? "#4e73df" : "#e3e6f0",
        boxShadow: checked ? "0 0 0 1px #4e73df" : "none",
        transition: "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
      }}
    >
      <div className="d-flex align-items-start gap-3">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 40,
            height: 40,
            backgroundColor: checked ? "#4e73df" : "#f0f2f8",
            color: checked ? "#fff" : "#5a5c69",
          }}
          aria-hidden="true"
        >
          <i className={`fa ${icon}`} />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span className="fw-semibold text-dark">
              {title}
              {required && (
                <span className="badge bg-secondary ms-2 fw-normal" style={{ fontSize: "0.65rem" }}>
                  Required
                </span>
              )}
            </span>
            <span
              className={`rounded-circle border d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                checked ? "bg-primary border-primary" : "bg-white"
              }`}
              style={{ width: 20, height: 20 }}
              aria-hidden="true"
            >
              {checked && <i className="fa fa-check text-white" style={{ fontSize: 10 }} />}
            </span>
          </div>
          <small className="text-muted d-block mt-1" style={{ lineHeight: 1.45 }}>
            {summary}
          </small>
        </div>
      </div>
    </button>
  );
};

const resolveUserProfile = (profileProp) => profileProp || UserService.getProfile() || {};

/** Normalize org API payloads: flat array, { data: [] }, or { data: { data: [] } }. */
const parseOrganizationRows = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.data)) return response.data.data;
  if (Array.isArray(response.data?.datas)) return response.data.datas;
  return [];
};

const mapOrganizationOptions = (rows) => {
  const opts = [];
  rows.forEach((o) => {
    const value = o.organization_uuid || o.value;
    if (!value) return;
    opts.push({
      label: convertUnderScoreStrings(o.name || o.organization_name || o.label || ""),
      value,
    });
  });
  return opts;
};

const getEmployeeCompanyName = (employee, companyNameMap = {}) => {
  if (!employee) return "—";
  if (employee.company && typeof employee.company === "object" && employee.company.name) {
    return employee.company.name;
  }
  if (employee.company_name) return employee.company_name;
  if (employee.company && typeof employee.company === "string" && isNaN(Number(employee.company))) {
    return employee.company;
  }
  const id =
    employee.company_id ??
    employee.company_uuid ??
    (employee.company != null ? String(employee.company) : null);
  if (id != null && companyNameMap[String(id)]) return companyNameMap[String(id)];
  if (id != null) return String(id);
  return "—";
};

const getEmployeeInitials = (employee) => {
  const first = (employee?.first_name || "").trim().charAt(0);
  const last = (employee?.last_name || "").trim().charAt(0);
  return (first + last).toUpperCase() || "?";
};

/** All known company identifiers for the source profile (for exclusion from target list). */
const getSourceCompanyIds = (employee) => {
  const ids = new Set();
  if (!employee) return ids;
  [
    employee.company_id,
    employee.company_uuid,
    employee.company?.company_uuid,
    employee.company?.company_id,
  ]
    .filter(Boolean)
    .forEach((id) => ids.add(String(id)));
  return ids;
};

const CloneToCompanyModal = (props) => {
  const {
    show,
    employee: presetEmployee,
    profile: profileProp,
    onClose,
    onCloneSuccess,
    Toast,
  } = props;

  const dispatch = useDispatch();
  const cloneInProgress = useSelector(selectCloneInProgress);
  const lastResult = useSelector(selectLastCloneResult);

  const userProfile = useMemo(() => resolveUserProfile(profileProp), [profileProp]);
  const isImpersonating = !!userProfile.is_impersonating;
  const isGlobalAdminUser = isGlobalAdmin(userProfile.role);
  const canSelectOrganization = isImpersonating || isGlobalAdminUser;
  const currentOrgId =
    OrganizationService.getOrgId() || userProfile.organization_id || "";

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [companyNameMap, setCompanyNameMap] = useState({});
  const patientSearchTimeoutRef = useRef(null);
  const patientResultsRef = useRef(null);

  const [orgOptions, setOrgOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([
    { label: "-- Select Company --", value: "" },
  ]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [formData, setFormData] = useState({
    target_organization_id: "",
    target_company_id: "",
    ...DEFAULT_COPY_OPTIONS,
    reason: "",
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [validation, setValidation] = useState({});

  const [enableOnlinePortal, setEnableOnlinePortal] = useState(false);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalEmailTaken, setPortalEmailTaken] = useState(false);
  const [checkingPortalEmail, setCheckingPortalEmail] = useState(false);
  const [portalEmailTouched, setPortalEmailTouched] = useState(false);
  const [checkingTargetCompany, setCheckingTargetCompany] = useState(false);
  const [targetCompanyBlocked, setTargetCompanyBlocked] = useState(false);
  const [targetCompanyBlockMessage, setTargetCompanyBlockMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workflowMode, setWorkflowMode] = useState(WORKFLOW_CLONE);

  const lockPatientSelection = !!presetEmployee?.employee_id;
  const sourceEmployee = selectedPatient;
  const isTransferMode = workflowMode === WORKFLOW_TRANSFER;

  const resetModalState = useCallback(() => {
    setValidation({});
    setAcknowledged(false);
    setEnableOnlinePortal(false);
    setPortalEmail("");
    setPortalEmailTaken(false);
    setCheckingPortalEmail(false);
    setPortalEmailTouched(false);
    setCheckingTargetCompany(false);
    setTargetCompanyBlocked(false);
    setTargetCompanyBlockMessage("");
    setIsSubmitting(false);
    setWorkflowMode(WORKFLOW_CLONE);
    setPatientSearchQuery("");
    setPatientSearchResults([]);
    setShowPatientResults(false);
    setFormData({
      target_organization_id: currentOrgId || "",
      target_company_id: "",
      ...DEFAULT_COPY_OPTIONS,
      reason: "",
    });
    setCompanyOptions([{ label: "-- Select Company --", value: "" }]);
    dispatch(clearLastCloneResult());
  }, [currentOrgId, dispatch]);

  const loadOrganizations = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      if (canSelectOrganization) {
        // Global admin or impersonating — full organizations list (LabList pattern).
        const orgResponse = await OrganizationService.getOrganization();
        const rows = parseOrganizationRows(orgResponse);
        setOrgOptions(mapOrganizationOptions(rows));

        // Pre-select current org when available, otherwise first row.
        const rowIds = rows.map((o) => o.organization_uuid).filter(Boolean);
        const defaultOrgId =
          (currentOrgId && rowIds.includes(currentOrgId) ? currentOrgId : null) ||
          rowIds[0] ||
          "";
        if (defaultOrgId) {
          setFormData((prev) => ({
            ...prev,
            target_organization_id: defaultOrgId,
          }));
        }
      } else if (currentOrgId) {
        let orgName = userProfile.organization_name;
        if (!orgName) {
          try {
            const org = await OrganizationService.getOrganizationById(currentOrgId);
            orgName = org?.name || org?.organization_name;
          } catch (e) {
            // non-fatal
          }
        }
        setOrgOptions([
          {
            label: convertUnderScoreStrings(orgName || "Current Organization"),
            value: currentOrgId,
          },
        ]);
        setFormData((prev) => ({
          ...prev,
          target_organization_id: currentOrgId,
        }));
      }
    } catch (e) {
      console.warn("[CloneToCompanyModal] org dropdown failed", e);
    } finally {
      setLoadingOrgs(false);
    }
  }, [currentOrgId, canSelectOrganization, userProfile.organization_name]);

  const loadCompaniesFor = useCallback(
    async (organizationUuid, sourceForExclusion = null) => {
      const orgId = organizationUuid || currentOrgId;
      if (!orgId) {
        setCompanyOptions([{ label: "-- Select Company --", value: "" }]);
        return;
      }

      const excludeCompanyIds = getSourceCompanyIds(sourceForExclusion);

      setLoadingCompanies(true);
      try {
        // Same endpoint as EmployeeFilter / AddEmployee — backend applies
        // DataSharingUtil → filters by the logged-in user's company_uuids.
        const data = await DropDownService.getCompanies(orgId);
        const opts = [{ label: "-- Select Company --", value: "" }];
        if (Array.isArray(data)) {
          data.forEach((c) => {
            const companyUuid = String(c.company_uuid || c.value || "");
            if (!companyUuid || excludeCompanyIds.has(companyUuid)) return;
            opts.push({
              label: c.name || c.company_name || c.label,
              value: companyUuid,
            });
          });
        } else if (data === false) {
          console.warn(
            "[CloneToCompanyModal] company dropdown skipped — organization id unavailable"
          );
        }
        setCompanyOptions(opts);

        // Clear target if it pointed at the source company (must pick a different one).
        setFormData((prev) =>
          prev.target_company_id && excludeCompanyIds.has(String(prev.target_company_id))
            ? { ...prev, target_company_id: "" }
            : prev
        );
      } catch (e) {
        console.warn("[CloneToCompanyModal] companies dropdown failed", e);
        setCompanyOptions([{ label: "-- Select Company --", value: "" }]);
      } finally {
        setLoadingCompanies(false);
      }
    },
    [currentOrgId]
  );

  useEffect(() => {
    if (!show) return;

    const loadCompanyNames = async () => {
      try {
        const orgId = currentOrgId || null;
        const companiesRes = await DropDownService.getCompanies(orgId);
        const cMap = {};
        if (Array.isArray(companiesRes)) {
          companiesRes.forEach((c) => {
            if (c.name != null) {
              if (c.company_uuid != null) cMap[c.company_uuid] = c.name;
              if (c.company_id != null) cMap[String(c.company_id)] = c.name;
              if (c.id != null) cMap[String(c.id)] = c.name;
            }
          });
        }
        setCompanyNameMap(cMap);
      } catch (e) {
        // non-blocking — fall back to inline company fields from search rows
      }
    };

    loadCompanyNames();
  }, [show, currentOrgId]);

  useEffect(() => {
    if (!show) return;

    setSelectedPatient(presetEmployee?.employee_id ? presetEmployee : null);
    resetModalState();
    loadOrganizations();
  }, [show, presetEmployee?.employee_id]);

  // When global admin / impersonating, ensure org is set once a patient is selected.
  useEffect(() => {
    if (!show || !canSelectOrganization || !sourceEmployee?.employee_id) return;
    if (formData.target_organization_id) return;

    const fallbackOrgId =
      currentOrgId ||
      sourceEmployee.organization_id ||
      orgOptions.find((o) => o.value)?.value ||
      "";
    if (fallbackOrgId) {
      setFormData((prev) => ({
        ...prev,
        target_organization_id: fallbackOrgId,
      }));
    }
  }, [
    show,
    canSelectOrganization,
    sourceEmployee?.employee_id,
    sourceEmployee?.organization_id,
    currentOrgId,
    formData.target_organization_id,
    orgOptions,
  ]);

  // Load data-sharing company list after patient is selected.
  useEffect(() => {
    if (!show || !sourceEmployee?.employee_id) return;

    if (canSelectOrganization) {
      if (!formData.target_organization_id) {
        setCompanyOptions([{ label: "-- Select Company --", value: "" }]);
        return;
      }
      loadCompaniesFor(formData.target_organization_id, sourceEmployee);
    } else if (currentOrgId) {
      loadCompaniesFor(currentOrgId, sourceEmployee);
    }
  }, [
    show,
    sourceEmployee?.employee_id,
    sourceEmployee?.company_id,
    canSelectOrganization,
    currentOrgId,
    formData.target_organization_id,
    loadCompaniesFor,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        patientResultsRef.current &&
        !patientResultsRef.current.contains(event.target)
      ) {
        setShowPatientResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (lockPatientSelection || !show) return;

    if (patientSearchTimeoutRef.current) {
      clearTimeout(patientSearchTimeoutRef.current);
    }

    if (patientSearchQuery.length < 1) {
      setPatientSearchResults([]);
      setShowPatientResults(false);
      return;
    }

    patientSearchTimeoutRef.current = setTimeout(async () => {
      setPatientSearching(true);
      setShowPatientResults(true);
      try {
        const response = await EmployeeService.getEmployeeList({
          search_query: patientSearchQuery,
          perPage: 10,
          page: 1,
        });
        const rows = response?.data?.data;
        setPatientSearchResults(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.warn("[CloneToCompanyModal] patient search failed", e);
        setPatientSearchResults([]);
      } finally {
        setPatientSearching(false);
      }
    }, 400);

    return () => {
      if (patientSearchTimeoutRef.current) {
        clearTimeout(patientSearchTimeoutRef.current);
      }
    };
  }, [patientSearchQuery, lockPatientSelection, show]);

  const changeHandler = (value, name) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "target_organization_id") {
        next.target_company_id = "";
      }
      return next;
    });
    setValidation((prev) => ({ ...prev, [name]: "" }));
    if (name === "target_company_id" || name === "target_organization_id") {
      setTargetCompanyBlocked(false);
      setTargetCompanyBlockMessage("");
    }
  };

  const toggleCopyOption = (key) => {
    if (key === COPY_REQUIRED_KEY) {
      return;
    }
    setFormData((prev) => ({ ...prev, [key]: !prev[key] }));
    setValidation((prev) => ({ ...prev, copyOptions: "" }));
  };

  const setAllCopyOptions = (value) => {
    setFormData((prev) => ({
      ...prev,
      clone_demographics: true,
      clone_insurance: value,
      clone_employer_forms: value,
    }));
    setValidation((prev) => ({ ...prev, copyOptions: "" }));
  };

  const hasCopySelection = !!formData.clone_demographics;

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setPatientSearchQuery("");
    setPatientSearchResults([]);
    setShowPatientResults(false);
    setEnableOnlinePortal(false);
    setPortalEmail("");
    setPortalEmailTaken(false);
    setPortalEmailTouched(false);
    setFormData((prev) => ({ ...prev, target_company_id: "" }));
    setValidation((prev) => ({ ...prev, selectedPatient: "", target_company_id: "", portalEmail: "" }));
    setTargetCompanyBlocked(false);
    setTargetCompanyBlockMessage("");
  };

  const handleClearPatient = () => {
    if (lockPatientSelection) return;
    setSelectedPatient(null);
    setPatientSearchQuery("");
    setPatientSearchResults([]);
    setEnableOnlinePortal(false);
    setPortalEmail("");
    setPortalEmailTaken(false);
    setPortalEmailTouched(false);
    setFormData((prev) => ({ ...prev, target_company_id: "" }));
    setTargetCompanyBlocked(false);
    setTargetCompanyBlockMessage("");
  };

  const isSameOrgClone = useMemo(() => {
    return sourceEmployee?.organization_id === formData.target_organization_id;
  }, [sourceEmployee, formData.target_organization_id]);

  const isSameCompanyClone = useMemo(() => {
    return sourceEmployee?.company_id === formData.target_company_id;
  }, [sourceEmployee, formData.target_company_id]);

  useEffect(() => {
    if (!show || !sourceEmployee?.employee_id || !formData.target_company_id) {
      setCheckingTargetCompany(false);
      setTargetCompanyBlocked(false);
      setTargetCompanyBlockMessage("");
      return;
    }

    if (isSameCompanyClone) {
      setCheckingTargetCompany(false);
      setTargetCompanyBlocked(true);
      setTargetCompanyBlockMessage("Choose a DIFFERENT company than the source.");
      return;
    }

    let cancelled = false;
    setCheckingTargetCompany(true);

    (async () => {
      try {
        const response = await EmployeeCloneService.checkTargetCompany(
          sourceEmployee.employee_id,
          { target_company_id: formData.target_company_id }
        );
        if (cancelled) return;

        const result = response?.data ?? response;
        const available = result?.available !== false;

        if (!available) {
          const message =
            result?.message ||
            "This patient already has a profile at the selected company.";
          setTargetCompanyBlocked(true);
          setTargetCompanyBlockMessage(message);
        } else {
          setTargetCompanyBlocked(false);
          setTargetCompanyBlockMessage("");
        }
      } catch (e) {
        if (!cancelled) {
          setTargetCompanyBlocked(false);
          setTargetCompanyBlockMessage("");
        }
      } finally {
        if (!cancelled) {
          setCheckingTargetCompany(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    show,
    sourceEmployee?.employee_id,
    formData.target_company_id,
    isSameCompanyClone,
  ]);

  const sourceHasPortal = !!(
    sourceEmployee?.user_id ||
    sourceEmployee?.portal_enabled
  );

  const needsCustomPortalEmail = sourceHasPortal || portalEmailTaken;

  useEffect(() => {
    if (!show || !enableOnlinePortal || !sourceEmployee?.email) {
      setPortalEmailTaken(false);
      setCheckingPortalEmail(false);
      return undefined;
    }

    if (sourceHasPortal) {
      setPortalEmailTaken(true);
      setCheckingPortalEmail(false);
      return undefined;
    }

    let cancelled = false;
    setCheckingPortalEmail(true);

    (async () => {
      try {
        const response = await EmployeeCloneService.checkPortalEmail({
          email: sourceEmployee.email,
        });
        if (cancelled) return;
        const taken = !!response?.data?.portal_email_taken;
        setPortalEmailTaken(taken);
        if (taken) {
          setPortalEmail("");
        }
      } catch (e) {
        if (!cancelled) {
          setPortalEmailTaken(false);
        }
      } finally {
        if (!cancelled) {
          setCheckingPortalEmail(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    show,
    enableOnlinePortal,
    sourceEmployee?.email,
    sourceEmployee?.employee_id,
    sourceHasPortal,
  ]);

  const resolvedPortalEmail = useMemo(() => {
    if (needsCustomPortalEmail) {
      return (portalEmail || "").trim();
    }
    return (sourceEmployee?.email || "").trim();
  }, [needsCustomPortalEmail, portalEmail, sourceEmployee?.email]);

  const livePortalEmailError = useMemo(
    () =>
      validatePortalEmailInput(
        portalEmail,
        sourceEmployee?.email,
        needsCustomPortalEmail
      ),
    [portalEmail, sourceEmployee?.email, needsCustomPortalEmail]
  );

  const displayedPortalEmailError =
    validation.portalEmail ||
    (portalEmailTouched && livePortalEmailError) ||
    null;

  const portalEmailLooksValid =
    needsCustomPortalEmail &&
    !!resolvedPortalEmail &&
    !livePortalEmailError;

  const formValid =
    !!sourceEmployee?.employee_id &&
    !!formData.target_organization_id &&
    !!formData.target_company_id &&
    hasCopySelection &&
    acknowledged &&
    !isSubmitting &&
    !cloneInProgress &&
    !checkingPortalEmail &&
    !checkingTargetCompany &&
    !isSameCompanyClone &&
    !targetCompanyBlocked &&
    (!enableOnlinePortal ||
      (needsCustomPortalEmail
        ? portalEmailLooksValid
        : !!sourceEmployee?.email));

  const handleClone = async () => {
    const errs = {};
    if (!sourceEmployee?.employee_id) errs.selectedPatient = "Select a patient first.";
    if (!formData.target_organization_id) errs.target_organization_id = "Required";
    if (!formData.target_company_id) errs.target_company_id = "Required";
    if (!formData.clone_demographics) {
      errs.copyOptions = "Demographics & identification is required to create the new profile.";
    }
    if (isSameCompanyClone) {
      errs.target_company_id = "Choose a DIFFERENT company than the source.";
    } else if (targetCompanyBlocked) {
      errs.target_company_id =
        targetCompanyBlockMessage ||
        "This patient already has a profile at the selected company.";
    }
    if (enableOnlinePortal) {
      if (!sourceEmployee?.email) {
        errs.portalEmail =
          "This patient has no email on file. Add an email to the source profile before enabling portal access.";
      } else if (needsCustomPortalEmail) {
        const portalErr = validatePortalEmailInput(
          resolvedPortalEmail,
          sourceEmployee.email,
          true
        );
        if (portalErr) {
          errs.portalEmail = portalErr;
          setPortalEmailTouched(true);
        }
      }
    }
    if (Object.keys(errs).length) {
      setValidation(errs);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        target_organization_id: formData.target_organization_id,
        target_company_id: formData.target_company_id,
        clone_demographics: true,
        clone_insurance: !!formData.clone_insurance,
        clone_employer_forms: !!formData.clone_employer_forms,
        reason: formData.reason || null,
      };

      const action = await dispatch(
        isTransferMode
          ? reassignEmployeeProfile({
              employeeId: sourceEmployee.employee_id,
              payload: {
                ...payload,
                archive_source_profile: true,
              },
            })
          : cloneEmployeeProfile({
              employeeId: sourceEmployee.employee_id,
              payload,
            })
      );

      const submitThunk = isTransferMode ? reassignEmployeeProfile : cloneEmployeeProfile;
      if (!submitThunk.fulfilled.match(action)) {
        Toast?.error?.(
          action.payload ||
            lastResult?.message ||
            (isTransferMode ? "Transfer failed." : "Clone failed.")
        );
        return;
      }

      let resultProfile = action.payload?.data;

      if (enableOnlinePortal && resultProfile?.employee_id) {
        const portalResponse = await EmployeeCloneService.enablePortal(
          resultProfile.employee_id,
          { portal_email: resolvedPortalEmail }
        );
        const updated = portalResponse?.data;
        if (updated?.employee_id && updated?.portal_enabled) {
          resultProfile = updated;
          Toast?.success?.(
            action.payload?.message ||
              (isTransferMode
                ? "Patient transferred and portal access enabled successfully."
                : "Profile cloned and portal access enabled successfully.")
          );
        } else {
          const portalErr =
            portalResponse?.data?.portal_email?.[0] ||
            portalResponse?.message ||
            "Portal access could not be enabled.";
          Toast?.success?.(
            action.payload?.message ||
              (isTransferMode ? "Patient transferred successfully." : "Profile cloned successfully.")
          );
          Toast?.error?.(portalErr);
          onCloneSuccess && onCloneSuccess(resultProfile);
          onClose && onClose();
          return;
        }
      } else {
        Toast?.success?.(
          action.payload?.message ||
            (isTransferMode ? "Patient transferred successfully." : "Profile cloned successfully.")
        );
      }

      onCloneSuccess && onCloneSuccess(resultProfile);
      onClose && onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      size="lg"
      dialogClassName="hResponsive modal-dialog-centered"
      contentClassName="h-100 d-flex flex-column overflow-hidden"
    >
      <div
        className="sh_modalBox1 d-flex flex-column h-100 overflow-hidden"
        style={{ backgroundColor: "#fff", minHeight: 0 }}
      >
        <Modal.Header closeButton className="flex-shrink-0 border-bottom py-3">
          <Modal.Title>
            <h6 className="text-lg mb-0 font-weight-bold">
              {isTransferMode
                ? "Transfer Patient to Another Company"
                : "Add Patient to Another Company"}
            </h6>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="flex-grow-1 overflow-auto py-3"
          style={{ minHeight: 0 }}
        >
          <div className="border rounded p-3 mb-3 bg-light">
            <label className="mb-2 text-md opacity-75 d-block fw-semibold">Workflow</label>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="clone_workflow"
                id="workflow_clone"
                checked={workflowMode === WORKFLOW_CLONE}
                onChange={() => {
                  setWorkflowMode(WORKFLOW_CLONE);
                  setAcknowledged(false);
                }}
              />
              <label className="form-check-label" htmlFor="workflow_clone">
                Add patient to another company — keep both profiles active
              </label>
            </div>
            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="radio"
                name="clone_workflow"
                id="workflow_transfer"
                checked={workflowMode === WORKFLOW_TRANSFER}
                onChange={() => {
                  setWorkflowMode(WORKFLOW_TRANSFER);
                  setAcknowledged(false);
                }}
              />
              <label className="form-check-label" htmlFor="workflow_transfer">
                Transfer patient to another company — retire the source profile
              </label>
            </div>
            {isTransferMode && (
              <div className="alert alert-warning mt-3 mb-0 py-2 text-sm">
                The source profile will be marked <strong>transferred</strong> and hidden from
                active lists. Historical visits stay on the source profile.
              </div>
            )}
          </div>

          {/* Patient selection */}
          <div className="form-group mb-3">
            <label className="mb-0 text-md opacity-75">
              Select Patient <span className="text-danger">*</span>
            </label>
            {!lockPatientSelection && !sourceEmployee && (
              <div className="position-relative mt-1">
                <input
                  type="text"
                  className={`form-control form-control-solid ${patientSearching ? "pe-5" : ""}`}
                  placeholder="Search patient by name..."
                  value={patientSearchQuery}
                  onChange={(e) => setPatientSearchQuery(e.target.value)}
                  onFocus={() => {
                    if (patientSearchResults.length > 0) setShowPatientResults(true);
                  }}
                />
                {patientSearching && (
                  <div
                    className="position-absolute top-50 end-0 translate-middle-y me-3"
                    style={{ pointerEvents: "none" }}
                  >
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-label="Loading"
                    />
                  </div>
                )}
                {showPatientResults && !sourceEmployee && (
                  <div
                    ref={patientResultsRef}
                    className="list-group position-absolute w-100 shadow-sm"
                    style={{ zIndex: 1050, maxHeight: 220, overflowY: "auto" }}
                  >
                    {patientSearching && (
                      <div className="list-group-item text-muted">Searching...</div>
                    )}
                    {!patientSearching && patientSearchResults.length === 0 && (
                      <div className="list-group-item text-muted">No patients found</div>
                    )}
                    {!patientSearching &&
                      patientSearchResults.map((patient) => {
                        const companyLabel = getEmployeeCompanyName(patient, companyNameMap);
                        return (
                        <button
                          key={patient.employee_id}
                          type="button"
                          className="list-group-item list-group-item-action"
                          onClick={() => handlePatientSelect(patient)}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div className="min-w-0">
                              <strong>
                                {patient.first_name} {patient.last_name}
                              </strong>
                              <br />
                              <small className="text-muted">
                                DOB: {patient.dob || "N/A"}
                                {patient.phone
                                  ? ` · ${formatPhoneNumberUS(patient.phone)}`
                                  : ""}
                              </small>
                            </div>
                            <small
                              className="text-muted text-end flex-shrink-0"
                              style={{ maxWidth: "45%", lineHeight: 1.35 }}
                              title={companyLabel}
                            >
                              {companyLabel}
                            </small>
                          </div>
                        </button>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
            {validation.selectedPatient && (
              <span className="text-danger m-1">{validation.selectedPatient}</span>
            )}
          </div>

          {sourceEmployee && (
            <div
              className="clone-source-profile border rounded-3 mb-3 overflow-hidden shadow-sm"
              style={{ backgroundColor: "#f8f9fc", borderColor: "#e3e6f0" }}
            >
              <div className="d-flex align-items-stretch">
                <div
                  className="flex-shrink-0"
                  style={{ width: 4, background: "linear-gradient(180deg, #4e73df 0%, #224abe 100%)" }}
                  aria-hidden="true"
                />
                <div className="flex-grow-1 p-3">
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div className="d-flex align-items-start gap-3 min-w-0">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold flex-shrink-0"
                        style={{
                          width: 48,
                          height: 48,
                          background: "linear-gradient(135deg, #4e73df 0%, #224abe 100%)",
                          fontSize: "0.9rem",
                          letterSpacing: "0.02em",
                        }}
                        aria-hidden="true"
                      >
                        {getEmployeeInitials(sourceEmployee)}
                      </div>
                      <div className="min-w-0">
                        <div
                          className="text-uppercase text-muted fw-semibold mb-1"
                          style={{ fontSize: "0.68rem", letterSpacing: "0.06em" }}
                        >
                          Source profile
                        </div>
                        <div className="merge-refined-name text-truncate">
                          {sourceEmployee.first_name} {sourceEmployee.last_name}
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          <div className="merge-refined-meta">
                            <span className="merge-refined-label">DOB</span>{" "}
                            {sourceEmployee.dob || "—"}
                          </div>
                          <div className="merge-refined-meta">
                            <span className="merge-refined-label">Phone</span>{" "}
                            {sourceEmployee.phone
                              ? formatPhoneNumberUS(sourceEmployee.phone)
                              : "—"}
                          </div>
                          {sourceEmployee.system_id && (
                            <div className="merge-refined-meta">
                              <span className="merge-refined-label">ID</span>{" "}
                              {sourceEmployee.system_id}
                            </div>
                          )}
                        </div>
                        <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
                          <span className="merge-refined-meta mb-0">
                            <span className="merge-refined-label">Current company</span>
                          </span>
                          <span
                            className="badge rounded-pill fw-normal px-2 py-1"
                            style={{
                              backgroundColor: "#e7f1ff",
                              color: "#224abe",
                              border: "1px solid #c5d9f8",
                              maxWidth: "100%",
                              whiteSpace: "normal",
                              textAlign: "left",
                              lineHeight: 1.35,
                            }}
                          >
                            {getEmployeeCompanyName(sourceEmployee, companyNameMap)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {!lockPatientSelection && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light border flex-shrink-0 d-inline-flex align-items-center gap-1"
                        onClick={handleClearPatient}
                        title="Choose a different patient"
                      >
                        <i className="fa fa-edit" aria-hidden="true" />
                        Change
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: org + company — disabled until patient selected */}
          <div className={`row ${!sourceEmployee ? "opacity-50" : ""}`}>
            <div className="col-md-6">
              <div className="form-group">
                <label className="mb-0 text-md opacity-75">
                  Target Organization <span className="text-danger">*</span>
                </label>
                {loadingOrgs ? (
                  <div className="placeholder-glow">
                    <span className="placeholder col-12" style={{ height: 38 }} />
                  </div>
                ) : (
                  <FormInputs
                    key={`clone-target-org-${orgOptions.length}-${formData.target_organization_id || "none"}`}
                    type="SelectSearchList"
                    className="form-control form-control-solid"
                    name="target_organization_id"
                    options={orgOptions}
                    value={formData.target_organization_id}
                    placeholder="Search organization"
                    readOnly={!canSelectOrganization || !sourceEmployee}
                    changeHandler={(v) => changeHandler(v, "target_organization_id")}
                  />
                )}
                {!canSelectOrganization && (
                  <small className="text-muted d-block mt-1">
                    Your current organization (data-sharing companies only).
                  </small>
                )}
                {validation.target_organization_id && (
                  <span className="text-danger m-1">{validation.target_organization_id}</span>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label className="mb-0 text-md opacity-75">
                  Target Company <span className="text-danger">*</span>
                </label>
                {loadingCompanies ? (
                  <div className="placeholder-glow">
                    <span className="placeholder col-12" style={{ height: 38 }} />
                  </div>
                ) : (
                  <FormInputs
                    key={`clone-target-company-${sourceEmployee?.employee_id || "none"}-${companyOptions.length}`}
                    type="SelectSearchList"
                    className="form-control form-control-solid"
                    name="target_company_id"
                    options={companyOptions}
                    value={formData.target_company_id}
                    readOnly={!sourceEmployee}
                    changeHandler={(v) => changeHandler(v, "target_company_id")}
                  />
                )}
                {!sourceEmployee && (
                  <small className="text-muted d-block mt-1">
                    Select a patient first to load available companies.
                  </small>
                )}
                {sourceEmployee && !loadingCompanies && companyOptions.length <= 1 && (
                  <small className="text-warning d-block mt-1">
                    No other companies available in your data-sharing list for this organization.
                  </small>
                )}
                {(validation.target_company_id || targetCompanyBlockMessage) && (
                  <span className="text-danger m-1">
                    {validation.target_company_id || targetCompanyBlockMessage}
                  </span>
                )}
                {checkingTargetCompany && formData.target_company_id && (
                  <small className="text-muted d-block mt-1">Checking target company…</small>
                )}
              </div>
            </div>
          </div>

          <div
            className={`mt-3 mb-2 ${!sourceEmployee ? "opacity-50 pe-none" : ""}`}
            aria-disabled={!sourceEmployee}
          >
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
              <div>
                <h6 className="mb-0 fw-semibold text-dark">What to copy</h6>
                <small className="text-muted">
                  Demographics & identification is always copied. Choose any additional sections below.
                </small>
              </div>
              {sourceEmployee && (
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0"
                    onClick={() => setAllCopyOptions(true)}
                  >
                    Select all
                  </button>
                  <span className="text-muted" aria-hidden="true">
                    |
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0 text-muted"
                    onClick={() => setAllCopyOptions(false)}
                  >
                    Clear optional
                  </button>
                </div>
              )}
            </div>

            <div className="d-flex flex-column gap-2">
              {COPY_OPTION_DEFS.map((option) => (
                <CopyOptionCard
                  key={option.key}
                  option={option}
                  checked={!!formData[option.key]}
                  onToggle={toggleCopyOption}
                  disabled={!sourceEmployee}
                />
              ))}
            </div>

            {validation.copyOptions && (
              <span className="text-danger d-block mt-2 small">{validation.copyOptions}</span>
            )}
          </div>

          <div
            className={`clone-portal-section border rounded-3 overflow-hidden mt-3 shadow-sm ${
              enableOnlinePortal ? "clone-portal-section--active" : ""
            }`}
            style={{
              backgroundColor: enableOnlinePortal ? "#f8fbff" : "#fff",
              borderColor: enableOnlinePortal ? "#c5d9f8" : "#e3e6f0",
            }}
          >
            <div className="d-flex align-items-stretch">
              <div
                className="flex-shrink-0"
                style={{
                  width: 4,
                  background: enableOnlinePortal
                    ? "linear-gradient(180deg, #1cc88a 0%, #13855c 100%)"
                    : "linear-gradient(180deg, #858796 0%, #60616f 100%)",
                }}
                aria-hidden="true"
              />
              <div className="flex-grow-1 p-3">
                <div className="d-flex align-items-start gap-3">
                  {/* <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 42,
                      height: 42,
                      backgroundColor: enableOnlinePortal ? "#e8f8f0" : "#f0f2f8",
                      color: enableOnlinePortal ? "#13855c" : "#5a5c69",
                    }}
                    aria-hidden="true"
                  >
                    <i className="fa fa-sign-in" />
                  </div> */}
                  <div className="flex-grow-1 min-w-0">
                    <div className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="enable_online_portal"
                        checked={enableOnlinePortal}
                        disabled={!sourceEmployee}
                        onChange={(e) => {
                          setEnableOnlinePortal(e.target.checked);
                          if (!e.target.checked) {
                            setPortalEmail("");
                            setPortalEmailTaken(false);
                            setCheckingPortalEmail(false);
                            setPortalEmailTouched(false);
                          }
                          setValidation((prev) => ({ ...prev, portalEmail: "" }));
                        }}
                      />
                      <label
                        className="form-check-label fw-semibold text-dark"
                        htmlFor="enable_online_portal"
                      >
                        Create online portal access for the new profile
                      </label>
                    </div>
                    <small className="text-muted d-block" style={{ lineHeight: 1.45 }}>
                      Creates a separate patient portal login for the cloned profile only. The source
                      profile is never changed.
                    </small>
                  </div>
                </div>

                {enableOnlinePortal && sourceEmployee && (
                  <div
                    className="mt-3 pt-3"
                    style={{ borderTop: "1px solid #e3e6f0" }}
                  >
                    {!sourceEmployee.email ? (
                      <div
                        className="d-flex align-items-start gap-2 rounded-3 px-3 py-2"
                        style={{
                          backgroundColor: "#fdecea",
                          border: "1px solid #f5c2c7",
                        }}
                      >
                        <i
                          className="fa fa-times-circle text-danger mt-1"
                          aria-hidden="true"
                        />
                        <div>
                          <div className="fw-semibold text-dark small mb-1">
                            Email required for portal access
                          </div>
                          <small className="text-muted d-block">
                            This patient has no email on file. Add an email to the source profile
                            before enabling portal access.
                          </small>
                        </div>
                      </div>
                    ) : checkingPortalEmail ? (
                      <div
                        className="d-flex align-items-center gap-2 rounded-3 px-3 py-2"
                        style={{ backgroundColor: "#eef4ff" }}
                      >
                        <span
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                          aria-label="Checking email"
                        />
                        <small className="text-muted mb-0">
                          Checking if profile email is available for portal login...
                        </small>
                      </div>
                    ) : needsCustomPortalEmail ? (
                      <div
                        className="rounded-3 p-3"
                        style={{
                          backgroundColor: "#fff",
                          border: "1px solid #f6c23e",
                          boxShadow: "inset 0 0 0 1px rgba(246, 194, 62, 0.15)",
                        }}
                      >
                        <div className="d-flex align-items-start gap-2 mb-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#fff8e6",
                              color: "#d39e00",
                            }}
                            aria-hidden="true"
                          >
                            <i className="fa fa-exclamation-triangle" />
                          </div>
                          <div className="min-w-0">
                            <div className="fw-semibold text-dark small mb-1">
                              {sourceHasPortal
                                ? "Source profile already has portal access"
                                : "Profile email already registered"}
                            </div>
                            <small className="text-muted d-block" style={{ lineHeight: 1.5 }}>
                              {sourceHasPortal ? (
                                <>
                                  The source login{" "}
                                  {sourceEmployee.email ? (
                                    <span
                                      className="badge rounded-pill fw-normal px-2 py-1 ms-1"
                                      style={{
                                        backgroundColor: "#f0f2f8",
                                        color: "#5a5c69",
                                        border: "1px solid #d1d3e2",
                                      }}
                                    >
                                      {sourceEmployee.email}
                                    </span>
                                  ) : null}{" "}
                                  cannot be reused. Choose a new login email for the cloned profile.
                                </>
                              ) : (
                                <>
                                  <span
                                    className="badge rounded-pill fw-normal px-2 py-1 me-1"
                                    style={{
                                      backgroundColor: "#f0f2f8",
                                      color: "#5a5c69",
                                      border: "1px solid #d1d3e2",
                                    }}
                                  >
                                    {sourceEmployee.email}
                                  </span>
                                  is already linked to another portal account. Enter a different
                                  email below.
                                </>
                              )}
                            </small>
                          </div>
                        </div>

                        <label
                          className="mb-1 text-md opacity-75 fw-semibold"
                          htmlFor="clone_portal_email"
                        >
                          New portal login email <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span
                            className={`input-group-text ${
                              displayedPortalEmailError
                                ? "border-danger"
                                : portalEmailLooksValid
                                  ? "border-success"
                                  : ""
                            }`}
                            style={{ backgroundColor: "#f8f9fc" }}
                          >
                            <i className="fa fa-envelope text-muted" aria-hidden="true" />
                          </span>
                          <input
                            id="clone_portal_email"
                            type="email"
                            className={`form-control form-control-solid ${
                              displayedPortalEmailError
                                ? "is-invalid"
                                : portalEmailLooksValid
                                  ? "is-valid"
                                  : ""
                            }`}
                            value={portalEmail}
                            onChange={(e) => {
                              setPortalEmail(e.target.value);
                              setValidation((prev) => ({ ...prev, portalEmail: "" }));
                            }}
                            onBlur={() => setPortalEmailTouched(true)}
                            placeholder="e.g. patient.newcompany@example.com"
                            disabled={isSubmitting}
                            autoComplete="off"
                          />
                        </div>
                        {displayedPortalEmailError ? (
                          <span className="text-danger small d-block mt-2">
                            <i className="fa fa-times-circle me-1" aria-hidden="true" />
                            {displayedPortalEmailError}
                          </span>
                        ) : portalEmailLooksValid ? (
                          <span className="text-success small d-block mt-2">
                            <i className="fa fa-check-circle me-1" aria-hidden="true" />
                            Email format looks good and is different from the profile email.
                          </span>
                        ) : (
                          <small className="text-muted d-block mt-2">
                            Must be a valid email and different from{" "}
                            <strong>{sourceEmployee.email}</strong>.
                          </small>
                        )}
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-start gap-2 rounded-3 px-3 py-2"
                        style={{
                          backgroundColor: "#e8f8f0",
                          border: "1px solid #b7e4c7",
                        }}
                      >
                        <i
                          className="fa fa-check-circle text-success mt-1"
                          aria-hidden="true"
                        />
                        <div className="min-w-0">
                          <div className="fw-semibold text-dark small mb-1">
                            Ready to use profile email
                          </div>
                          <small className="text-muted d-block" style={{ lineHeight: 1.5 }}>
                            Portal login will use{" "}
                            <span
                              className="badge rounded-pill fw-normal px-2 py-1"
                              style={{
                                backgroundColor: "#fff",
                                color: "#13855c",
                                border: "1px solid #b7e4c7",
                              }}
                            >
                              {sourceEmployee.email}
                            </span>
                            . No separate email entry needed.
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="alert alert-warning mt-3 mb-2" role="alert" aria-live="polite">
            <strong>Operational data is never copied.</strong>
            <br />
            <small>
              Visits, EMR, billing, prescriptions, and compliance history remain attached
              only to the source profile.
            </small>
          </div>

          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="ack_portal_warning"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="ack_portal_warning">
              {isTransferMode
                ? "I understand the source profile will be retired, historical visits remain on the source, and the new profile will not inherit operational data."
                : "I understand that operational data will not be copied to the new profile."}
            </label>
          </div>

          <div className="form-group mt-3">
            <label className="mb-0 text-md opacity-75">Reason (optional)</label>
            <textarea
              className="form-control"
              name="reason"
              rows={2}
              value={formData.reason}
              maxLength={500}
              onChange={(e) => changeHandler(e.target.value, "reason")}
              placeholder="e.g. New employer enrollment for the same patient"
            />
          </div>

          {isSameOrgClone && sourceEmployee && (
            <div className="alert alert-info mt-2 mb-0 py-2 text-sm">
              You are creating a new profile inside the <strong>same organization</strong>.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="flex-shrink-0 border-top d-flex justify-content-between py-3">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting || cloneInProgress}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleClone} disabled={!formValid}>
            {isSubmitting || cloneInProgress
              ? enableOnlinePortal
                ? isTransferMode
                  ? "Transferring & enabling portal..."
                  : "Cloning & enabling portal..."
                : isTransferMode
                  ? "Transferring..."
                  : "Cloning..."
              : enableOnlinePortal
                ? isTransferMode
                  ? "Transfer & Enable Portal"
                  : "Clone & Enable Portal"
                : isTransferMode
                  ? "Transfer Patient"
                  : "Clone Profile"}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default WithRouter(WithRoutify(CloneToCompanyModal));
